import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { Customer } from '../../../shared/types/Customer';
import { getTenant } from '../../../theme/tokens';
import { makeCompanyName, makeEmail, makePhone, profileCity, profileStreet, profileCountry } from '../../../shared/config/localeProfile';
export function Customers() {
  const {
    t
  } = useTranslation('customers');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const {
    code: currencyCode
  } = getTenant().currency;
  const locale = getTenant().locale.default;
  const formatAmount = (amount: number) => `${currencyCode} ${amount.toLocaleString(locale)}`;
  const customers: Customer[] = [{
    id: '1',
    name: makeCompanyName('Acme Trading'),
    email: makeEmail('billing', 'acme'),
    phone: makePhone('33 4567'),
    address: profileStreet(0),
    city: profileCity(0),
    country: profileCountry(),
    createdAt: '2023-06-15',
    totalSpent: 875000,
    invoiceCount: 12,
    status: 'active'
  }, {
    id: '2',
    name: makeCompanyName('Tech Solutions'),
    email: makeEmail('accounts', 'techsolutions'),
    phone: makePhone('32 1234'),
    address: profileStreet(1),
    city: profileCity(1),
    country: profileCountry(),
    createdAt: '2023-08-20',
    totalSpent: 450000,
    invoiceCount: 8,
    status: 'active'
  }, {
    id: '3',
    name: makeCompanyName('Atoll Enterprises'),
    email: makeEmail('finance', 'atollent'),
    phone: makePhone('68 9 7890'),
    address: profileStreet(2),
    city: profileCity(2),
    country: profileCountry(),
    createdAt: '2023-04-10',
    totalSpent: 1250000,
    invoiceCount: 15,
    status: 'active'
  }, {
    id: '4',
    name: makeCompanyName('Innovation Hub'),
    email: makeEmail('billing', 'innovationhub'),
    phone: makePhone('33 8901'),
    address: profileStreet(3),
    city: profileCity(3),
    country: profileCountry(),
    createdAt: '2023-09-05',
    totalSpent: 180000,
    invoiceCount: 4,
    status: 'inactive'
  }];
  const getStatusBadge = (status: Customer['status']) => {
    const styles = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status]}`} data-id="element-4892">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || customer.email.toLowerCase().includes(searchTerm.toLowerCase()) || customer.phone && customer.phone.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const totalCustomers = filteredCustomers.length;
  const activeCustomers = filteredCustomers.filter(c => c.status === 'active').length;
  const totalRevenue = filteredCustomers.reduce((sum, c) => sum + c.totalSpent, 0);
  const avgRevenue = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-4893">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-4894">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4895">
          {t('pages.Customers.customers')}
        </h1>
        <button onClick={() => navigate('/customer-form')} className="px-4 py-2 bg-[#00539B] hover:bg-[#00457F] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-4896">
          {t('pages.Customers.add_customer')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-4897">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4898">
          <div className="text-sm font-medium text-gray-500" data-id="element-4899">
            {t('pages.Customers.total_customers')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-4900">
            {totalCustomers}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4901">
          <div className="text-sm font-medium text-gray-500" data-id="element-4902">
            {t('pages.Customers.active_customers')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-green-600" data-id="element-4903">
            {activeCustomers}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4904">
          <div className="text-sm font-medium text-gray-500" data-id="element-4905">
            {t('pages.Customers.total_revenue')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-4906">
            {formatAmount(totalRevenue)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-4907">
          <div className="text-sm font-medium text-gray-500" data-id="element-4908">
            {t('pages.Customers.avg_revenuecustomer')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-4909">
            {formatAmount(Math.round(avgRevenue))}
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-4910">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-4911">
          {/* Status Filter Tabs */}
          <div className="flex space-x-6" data-id="element-4912">
            <button onClick={() => setStatusFilter('all')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'all' ? 'border-[#00539B] text-[#003563]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4913">
              {t('pages.Customers.all')}
            </button>
            <button onClick={() => setStatusFilter('active')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'active' ? 'border-[#00539B] text-[#003563]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4914">
              {t('pages.CustomerForm.active')}
            </button>
            <button onClick={() => setStatusFilter('inactive')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${statusFilter === 'inactive' ? 'border-[#00539B] text-[#003563]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4915">
              {t('pages.CustomerForm.inactive')}
            </button>
          </div>

          {/* Search */}
          <div className="relative w-64" data-id="element-4916">
            <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-4917" />

            <input type="search" aria-label={t('pages.Customers.attrs.search_by_customer')} placeholder={t('pages.Customers.attrs.search_by_customer')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00539B]/20 focus:border-[#00539B] transition-colors" data-id="element-4918" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-4919">
          <table className="w-full text-left border-collapse" data-id="element-4920">
            <thead data-id="element-4921">
              <tr className="border-b border-gray-100" data-id="element-4922">
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4923">
                  {t('pages.Customers.customer')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4924">
                  {t('pages.Customers.contact')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4925">
                  {t('pages.Customers.location')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4926">
                  {t('pages.CustomerDetail.total_spent')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4927">
                  {t('pages.CustomerDetail.invoices')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-4928">
                  {t('pages.CustomerDetail.status_2')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-4929">
              {filteredCustomers.map(customer => <tr key={customer.id} onClick={() => navigate(`/customer-detail/${customer.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-4930">
                  <td className="px-6 py-4" data-id="element-4931">
                    <div className="text-sm font-medium text-gray-900 group-hover:text-[#003563] transition-colors" data-id="element-4932">
                      {customer.name}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5" data-id="element-4933">
                      {t('pages.Customers.since')}{' '}
                      {new Date(customer.createdAt).toLocaleDateString('en-GB')}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4934">
                    <div className="text-sm text-gray-900" data-id="element-4935">
                      <div className="flex items-center gap-1.5 mb-1" data-id="element-4936">
                        <MailIcon size={13} className="text-gray-400" data-id="element-4937" />
                        {customer.email}
                      </div>
                      {customer.phone && <div className="flex items-center gap-1.5" data-id="element-4938">
                          <PhoneIcon size={13} className="text-gray-400" data-id="element-4939" />
                          {customer.phone}
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4940">
                    <div className="text-sm text-gray-900" data-id="element-4941">{customer.city}</div>
                    <div className="text-sm text-gray-500 mt-0.5" data-id="element-4942">
                      {customer.country}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4943">
                    <div className="text-sm font-medium text-gray-900" data-id="element-4944">
                      {formatAmount(customer.totalSpent)}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4945">
                    <div className="text-sm text-gray-900" data-id="element-4946">
                      {customer.invoiceCount}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-4947">
                    {getStatusBadge(customer.status)}
                  </td>
                </tr>)}
              {filteredCustomers.length === 0 && <tr data-id="element-4948">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-4949">
                    {t('pages.Customers.no_customers_found_matching_your_criteria')}
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}